<template>
  <div class="px-4 pb-4 tw">
    <!-- Add / Edit panel -->
    <LienFormPanel
      :model-value="panelOpen"
      @update:model-value="
        (val: boolean) => {
          if (!val) closePanel();
          else panelOpen = val;
        }
      "
      :matter-id="matterId"
      :matter-name="matterName"
      :lien="activeLien"
      :tracking="props.tracking"
      @saved="onSaved"
    />

    <!-- Summary bar -->
    <div
      v-if="summary && liens.length"
      class="grid divide-x divide-gray-200 bg-white border border-gray-200 rounded-lg px-0 mb-4"
      :class="showQboColumn ? 'grid-cols-5' : 'grid-cols-4'"
      data-cy="liens-summary-bar"
    >
      <div v-for="metric in summaryMetrics" :key="metric.label" class="px-7 py-5">
        <div
          class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1"
        >
          {{ metric.label }}
        </div>
        <div class="text-2xl font-bold tabular-nums" :class="metric.valueClass">
          {{ metric.value }}
        </div>
        <div v-if="metric.sublabel" class="text-xs text-gray-500 mt-0.5">
          {{ metric.sublabel }}
        </div>
      </div>
      <!-- QBO: Unpushed (Finalized) — only when connected -->
      <div v-if="showQboColumn" class="px-7 py-5" data-cy="liens-unpushed-card">
        <div
          class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1"
        >
          Unsubmitted (Finalized)
        </div>
        <div
          class="text-2xl font-bold tabular-nums"
          :class="unpushedSummary.count > 0 ? 'text-error' : 'text-gray-900'"
        >
          {{ unpushedSummary.count }}
        </div>
        <div v-if="unpushedSummary.count > 0" class="text-xs text-gray-500 mt-0.5">
          {{ formatCurrency(unpushedSummary.value) }} awaiting submission
        </div>
      </div>
    </div>
    <!-- Skeleton loader (initial load only) -->
    <template v-if="uiState.loading && !liens.length && !summary">
      <div data-testid="skeleton">
        <v-skeleton-loader type="heading" class="mb-4" elevation="0" />
        <v-skeleton-loader
          type="table-row-divider@5"
          class="w-full"
          elevation="0"
        />
      </div>
    </template>

    <!-- Error state -->
    <template v-else-if="uiState.error && !uiState.loading">
      <div
        class="flex items-center gap-3 bg-[rgba(var(--v-theme-error),0.08)] border border-[rgb(var(--v-theme-error))] rounded-lg px-4 py-3 text-sm text-error mt-4"
        data-cy="liens-error-banner"
      >
        <v-icon size="18" color="error">mdi-alert-circle</v-icon>
        Unable to load liens. Try refreshing.
      </div>
    </template>

    <!-- Empty state -->
    <template v-else-if="!liens.length">
      <div class="text-center py-16 text-gray-500">
        <div class="text-5xl mb-4 opacity-40">📋</div>
        <h3 class="text-base font-semibold text-gray-900 mb-2">
          No liens recorded for this matter
        </h3>
        <p class="text-sm mb-5 max-w-md mx-auto">
          Liens allow you to track amounts owed to medical providers, insurance
          companies, and other third parties from the settlement.
        </p>
        <LlTooltip location="top" :disabled="!isSettlementLocked">
          <template #default>
            <span style="white-space: pre-line;">{{ lockTooltip }}</span>
          </template>
          <template #activator>
            <LlSecondaryButton
              color="primary"
              prepend-icon="mdi-plus"
              :disabled="isSettlementLocked"
              data-cy="add-lien-empty"
              @click="openAdd"
            >
              Add Lien
            </LlSecondaryButton>
          </template>
        </LlTooltip>
      </div>
    </template>

    <!-- Table with data -->
    <template v-else>
      <!-- Settlement-locked notice — explains why row actions are disabled
           (tooltips don't fire on disabled controls, so surface the reason). -->
      <AlertBar
        v-if="isSettlementLocked"
        type="info"
        variant="tonal"
        class="mb-4"
        data-cy="liens-locked-banner"
      >
        <span class="font-weight-bold">Liens are locked.</span>
        These lien amounts have been finalized in the settlement calculator.
        Restart the settlement flow to make changes.
      </AlertBar>

      <!-- Action bar -->
      <div class="flex items-center justify-end mb-4">
        <LlTooltip location="top" :disabled="!isSettlementLocked">
          <template #default>
            <span style="white-space: pre-line;">{{ lockTooltip }}</span>
          </template>
          <template #activator>
            <LlSecondaryButton
              color="primary"
              prepend-icon="mdi-plus"
              :disabled="isSettlementLocked"
              data-cy="add-lien"
              @click="openAdd"
            >
              Add Lien
            </LlSecondaryButton>
          </template>
        </LlTooltip>
      </div>

      <!-- Bulk action bar (always in DOM, buttons disabled when nothing selected) -->
      <div
        class="flex flex-wrap items-center justify-between gap-3 px-4 py-3 mb-3 bg-gray-50 border border-gray-200 rounded-lg"
        data-cy="lien-bulk-action-bar"
      >
        <span
          v-show="selectedIds.length > 0"
          class="text-sm font-medium px-2.5 py-1 rounded-md border text-green-700 bg-green-50 border-green-600"
          data-cy="lien-selection-badge"
        >
          {{ pluralizeWithCount(selectedIds.length, 'lien') }} selected
        </span>
        <span v-show="selectedIds.length === 0" class="text-sm text-gray-400">
          Select liens to submit, undo, or delete in bulk
        </span>

        <div class="flex items-center gap-2 flex-wrap">
          <LlTooltip
            v-if="showQboColumn"
            :text="!hasPushableSelected && blockedSelectedCount > 0 ? BULK_PUSH_ALL_BLOCKED : ''"
            location="bottom"
          >
            <template #activator>
              <LlSecondaryButton
                variant="outlined"
                color="primary"
                :disabled="!hasPushableSelected || isBulkPushing"
                :loading="isBulkPushing"
                data-cy="bulk-push-selected"
                @click="handleBulkPush"
              >
                <QboIcon v-if="!isBulkPushing" :size="16" class="mr-2" />
                Submit Selected to QuickBooks
              </LlSecondaryButton>
            </template>
          </LlTooltip>
          <LlSecondaryButton
            v-if="showQboColumn"
            variant="outlined"
            color="orange-darken-1"
            :disabled="!hasUndoableSelected || isBulkUndoing"
            :loading="isBulkUndoing"
            data-cy="bulk-undo-selected"
            @click="handleBulkUndo"
          >
            <v-icon v-if="!isBulkUndoing" size="16" class="mr-1">mdi-undo</v-icon>
            Undo Selected in QuickBooks
          </LlSecondaryButton>
          <LlSecondaryButton
            variant="outlined"
            color="error"
            prepend-icon="mdi-delete"
            :disabled="selectedIds.length === 0 || isSettlementLocked"
            data-cy="bulk-delete-selected"
            @click="handleBulkDelete"
          >
            Delete
          </LlSecondaryButton>
          <LlSecondaryButton
            variant="outlined"
            :disabled="selectedIds.length === 0"
            data-cy="bulk-clear-selection"
            @click="clearSelection"
          >
            Clear Selection
          </LlSecondaryButton>
        </div>
      </div>

      <!-- Table -->
      <div class="bg-white rounded-lg shadow overflow-hidden relative">
        <!-- Re-fetch overlay (sort/page change) — the skeleton above only covers the initial load -->
        <div
          v-if="uiState.loading"
          class="absolute inset-0 z-10 flex items-center justify-center bg-white/60"
          data-cy="liens-loading-overlay"
        >
          <v-progress-circular indeterminate size="32" width="3" color="success" />
        </div>
        <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 w-10" @click.stop>
                <LlCheckbox
                  :model-value="allSelected"
                  :indeterminate="someSelected"
                  aria-label="Select all liens"
                  :disabled="isSettlementLocked && !hasAnyPushableOrUndoable"
                  data-cy="lien-select-all"
                  @update:model-value="(v: boolean | null) => toggleAll(!!v)"
                />
              </th>
              <th
                v-for="col in COLUMN_DEFS"
                :key="col.orderBy"
                :class="[COLUMN_ALIGN_CLASS[col.align], 'px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none']"
                :data-cy="col.dataCy"
                @click="onSortClick(col.orderBy!)"
              >
                {{ col.label }}
                <v-icon
                  v-if="sortIcon(col.orderBy!)"
                  :icon="sortIcon(col.orderBy!)"
                  size="14"
                  class="ml-1"
                  data-cy="lien-sort-arrow"
                />
              </th>
              <th
                class="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr
              v-for="lien in liens"
              :key="lien.lienId"
              :class="[
                isSettlementLocked ? '' : 'cursor-pointer hover:bg-gray-50',
                activeLien?.lienId === lien.lienId && panelOpen
                  ? 'bg-green-50 hover:bg-green-50 border-l-4 border-l-green-600'
                  : bulkErrors[lien.lienId]
                    ? 'bg-[rgba(var(--v-theme-error),0.08)] hover:!bg-[rgba(var(--v-theme-error),0.08)] border-l-4 border-l-[rgb(var(--v-theme-error))]'
                    : isSelected(lien.lienId)
                      ? 'bg-green-50 hover:bg-green-50'
                      : '',
              ]"
              :data-cy="isSelected(lien.lienId) ? 'lien-row-selected' : 'lien-row'"
              @click="openEdit(lien)"
            >
              <td class="px-4 py-3 w-10" @click.stop>
                <LlCheckbox
                  :model-value="isSelected(lien.lienId)"
                  :aria-label="`Select ${lien.vendorDisplayName}`"
                  :disabled="
                    isRowSelectionLocked(lien) ||
                    (isPushed(lien.status) && !canUndoLien(lien))
                  "
                  data-cy="lien-row-checkbox"
                  @update:model-value="toggle(lien.lienId)"
                />
              </td>
              <td class="px-6 py-3 text-sm">
                <div class="font-medium text-gray-900">
                  {{ lien.vendorDisplayName }}
                </div>
              </td>
              <td class="px-6 py-3 text-sm text-gray-500">
                {{ lien.description || '—' }}
              </td>
              <td class="px-6 py-3 text-sm text-gray-700">
                {{ lien.lienTypeDisplayName }}
              </td>
              <td class="px-6 py-3 text-sm text-gray-700 text-right">
                {{ formatCurrency(lien.originalAmount) }}
              </td>
              <td class="px-6 py-3 text-sm text-gray-700 text-right">
                <template v-if="lien.negotiatedAmount != null">
                  {{ formatCurrency(lien.negotiatedAmount) }}
                </template>
                <span v-else class="text-gray-300">—</span>
              </td>
              <td
                class="px-6 py-3 text-sm font-semibold text-gray-900 text-right"
              >
                {{ formatCurrency(lien.currentAmount) }}
                <div
                  v-if="lien.negotiatedAmount != null"
                  class="text-xs font-medium text-green-600 mt-0.5"
                >
                  {{ formatCurrency(lien.savings) }} ({{ Number(lien.savingsPercentage).toFixed(2) }}%)
                </div>
              </td>
              <td class="px-6 py-3 text-sm text-gray-700" data-cy="lien-start-date">
                <span :class="{ 'text-gray-300': !lien.dateOfService }">
                  {{ fmtDate(lien.dateOfService) }}
                </span>
              </td>
              <td class="px-6 py-3 text-sm text-gray-700" data-cy="lien-end-date">
                <span :class="{ 'text-gray-300': !lien.dateOfServiceEnd }">
                  {{ fmtDate(lien.dateOfServiceEnd) }}
                </span>
              </td>
              <td class="px-6 py-3 text-center">
                <LlTooltip location="top" :disabled="!isSettlementLocked && !isPushed(lien.status)">
                  <template #default>
                    <span style="white-space: pre-line;">{{ isSettlementLocked ? lockTooltip : pushedStatusTooltip }}</span>
                  </template>
                  <template #activator>
                <div class="inline-flex" @click.stop>
                <v-menu :disabled="isSettlementLocked || isPushed(lien.status)">
                  <template #activator="{ props: statusMenuProps }">
                    <v-chip
                      v-bind="statusMenuProps"
                      size="small"
                      rounded="pill"
                      :disabled="isSettlementLocked || isPushed(lien.status)"
                      class="lien-status-badge font-weight-medium"
                      :class="[
                        `lien-status--${lien.status.toLowerCase()}`,
                        (isSettlementLocked || isPushed(lien.status)) ? '' : 'cursor-pointer',
                      ]"
                      data-cy="lien-status-chip"
                    >
                      {{ LIEN_STATUS_LABELS[lien.status.toLowerCase()] ?? lien.status }}
                      <v-icon size="12" class="lien-status-caret">mdi-chevron-down</v-icon>
                    </v-chip>
                  </template>
                  <v-list density="compact" class="!rounded-lg !shadow-xl min-w-[160px] !py-1">
                    <v-list-item
                      v-for="opt in LIEN_STATUS_OPTIONS"
                      :key="opt.value"
                      :class="['transition-colors', lien.status === opt.value ? 'font-semibold' : 'text-gray-700']"
                      :style="lien.status === opt.value ? STATUS_STYLES[opt.value] : {}"
                      @click="changeStatus(lien, opt.value)"
                    >
                      <template #prepend>
                        <span
                          class="w-2 h-2 rounded-full flex-shrink-0 mr-2"
                          :style="{ backgroundColor: STATUS_DOT_COLORS[opt.value] ?? '#94a3b8' }"
                        />
                      </template>
                      <v-list-item-title class="text-sm">{{ opt.label }}</v-list-item-title>
                    </v-list-item>
                    <template v-if="showQboColumn && isPushActionable(lien)">
                      <v-divider class="my-1" />
                      <v-list-item
                        class="text-primary"
                        data-cy="lien-status-push-action"
                        :disabled="isRowPushing(lien.lienId)"
                        @click="handleRowPush(lien)"
                      >
                        <v-list-item-title class="flex items-center gap-1.5 text-sm">
                          <QboIcon :size="16" />
                          {{ pushActionLabel(lien) }}
                        </v-list-item-title>
                      </v-list-item>
                    </template>
                    <template v-else-if="showQboColumn && isPushBlocked(lien)">
                      <v-divider class="my-1" />
                      <LienPushBlockedItem :reason="lien.pushBlockedReason ?? ''" data-cy="lien-status-push-action-blocked" />
                    </template>
                  </v-list>
                </v-menu>
                </div>
                  </template>
                </LlTooltip>
                <!-- QBO push metadata — only when QBO integration is active -->
                <template v-if="showQboColumn">
                  <div v-if="isRowPushing(lien.lienId) || isRowUndoing(lien.lienId)" class="flex items-center justify-center gap-1.5 text-xs text-gray-500 mt-1">
                    <v-progress-circular indeterminate size="12" width="2" color="success" />
                    {{ isRowUndoing(lien.lienId) ? 'Undoing…' : 'Submitting...' }}
                  </div>
                  <!-- pushed → QBO transaction ref -->
                  <div
                    v-else-if="isPushed(lien.status) && lien.qboTransactionId"
                    class="text-xs text-gray-400 tabular-nums mt-1"
                    data-cy="lien-qbo-ref"
                  >
                    {{ lien.qboTransactionId }}
                  </div>
                  <!-- failed → details link opens the error modal -->
                  <LlSecondaryButton
                    v-else-if="pushBadgeState(lien) === 'failed'"
                    variant="text"
                    color="error"
                    size="x-small"
                    class="mt-1"
                    data-cy="lien-error-details"
                    @click.stop="openErrorDetail(lien)"
                  >
                    Submit failed · Details
                  </LlSecondaryButton>
                  <!-- inline error from the most recent bulk push/undo -->
                  <div
                    v-if="bulkErrors[lien.lienId]"
                    class="flex items-start gap-1 text-xs text-error mt-1"
                    data-cy="lien-bulk-error"
                  >
                    <v-icon size="13" color="error" class="mt-0.5 flex-shrink-0">mdi-alert-circle</v-icon>
                    <span style="white-space: normal">{{ bulkErrors[lien.lienId] }}</span>
                  </div>
                </template>
              </td>
              <td class="px-6 py-3 text-center" @click.stop>
                <!-- When locked with nothing to edit/push/undo, the trigger is styled disabled
                     (not the native `disabled` attribute) so its hover tooltip still fires — a
                     genuinely disabled button suppresses hover events, same reason the checkbox
                     tooltip attempt above was abandoned. Clicking still opens the menu, since its
                     items (including the reconciled/cleared "Undo Push" tooltip) are already
                     individually disabled/gated and stay reachable that way. -->
                <LlTooltip
                  :disabled="!isRowSelectionLocked(lien) || isRowPushing(lien.lienId) || isRowUndoing(lien.lienId)"
                  location="left"
                >
                  <template #default>
                    <div style="max-width: 260px">
                      {{ lien.undoBlockedReason || lien.pushBlockedReason || 'This lien is locked and cannot be edited or acted on right now.' }}
                    </div>
                  </template>
                  <template #activator>
                    <v-menu>
                      <template #activator="{ props: menuProps }">
                        <LlSecondaryButton
                          icon="mdi-dots-vertical"
                          variant="text"
                          size="small"
                          density="comfortable"
                          aria-label="Lien actions"
                          v-bind="menuProps"
                          :disabled="isRowSelectionLocked(lien) || isRowPushing(lien.lienId) || isRowUndoing(lien.lienId)"
                          data-cy="lien-actions"
                        />
                      </template>
                      <v-list density="compact">
                        <v-list-item :disabled="isSettlementLocked" @click="openEdit(lien)">
                          <v-list-item-title>Edit</v-list-item-title>
                        </v-list-item>
                        <!-- QBO push row actions -->
                        <v-list-item
                          v-if="showQboColumn && isPushActionable(lien)"
                          class="text-primary"
                          data-cy="lien-push-action"
                          :disabled="isRowPushing(lien.lienId)"
                          @click="handleRowPush(lien)"
                        >
                          <v-list-item-title class="flex items-center gap-1.5">
                            <QboIcon :size="16" />
                            {{ pushActionLabel(lien) }}
                          </v-list-item-title>
                        </v-list-item>
                        <!-- Trust too short to cover this lien — shown disabled with the shortfall, not hidden. -->
                        <LienPushBlockedItem
                          v-else-if="showQboColumn && isPushBlocked(lien)"
                          :reason="lien.pushBlockedReason ?? ''"
                          data-cy="lien-push-action-blocked"
                        />
                        <v-list-item
                          v-if="showQboColumn && canUndoLien(lien)"
                          data-cy="lien-undo-action"
                          :disabled="isRowUndoing(lien.lienId)"
                          @click="handleRowUndo(lien)"
                        >
                          <v-list-item-title class="flex items-center gap-1.5">
                            <v-icon size="16">mdi-undo</v-icon>
                            Undo Submit
                          </v-list-item-title>
                        </v-list-item>
                        <!-- Pushed but proactively blocked (sc-46091: QBO reports Cleared/Reconciled) —
                             shown disabled with a tooltip explaining why, rather than hidden. -->
                        <LlTooltip v-else-if="showQboColumn && isPushed(lien.status)" location="left">
                          <template #default>
                            <div style="max-width: 260px">
                              {{ lien.undoBlockedReason || 'This lien cannot be undone right now.' }}
                            </div>
                          </template>
                          <template #activator>
                            <v-list-item class="text-gray-400" data-cy="lien-undo-action-blocked">
                              <v-list-item-title class="flex items-center gap-1.5">
                                <v-icon size="16">mdi-undo</v-icon>
                                Undo Push
                              </v-list-item-title>
                            </v-list-item>
                          </template>
                        </LlTooltip>
                        <!-- Hidden once pushed to QBO — must Undo first to avoid orphaning the trust check. -->
                        <v-list-item
                          v-if="!isPushed(lien.status)"
                          class="text-error"
                          :disabled="isSettlementLocked"
                          data-cy="lien-delete-action"
                          @click="confirmDelete(lien)"
                        >
                          <v-list-item-title>Delete</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </template>
                </LlTooltip>
              </td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>

      <!-- Pagination -->
      <div class="relative z-10">
        <SimplePagination
          :current-page="currentPage"
          :per-page="lienStore.pagination.limit"
          :total-items="count"
          :offset="lienStore.pagination.offset"
          :hide-show-all="true"
          @pagechanged="onPageChange"
        />
      </div>
    </template>

    <!-- Delete confirmation dialog -->
    <LlConfirmationDialog
      ref="deleteConfirmRef"
      title="Delete Lien"
      :message="deleteMessage"
      confirm-text="Delete"
      toggle-text="Cancel"
      confirm-color="error"
      :show-warning-icon="true"
      data-cy="delete-lien-confirmation"
    />

    <!-- Single undo confirmation -->
    <LlConfirmationDialog
      ref="undoConfirmRef"
      title="Undo Submit"
      :message="undoDialog.message"
      confirm-text="Delete Check & Undo Submit"
      toggle-text="Cancel"
      confirm-color="error"
      :show-warning-icon="true"
      preserve-newlines
      data-cy="undo-push-confirmation"
    />

    <!-- Single push confirmation -->
    <LienPushConfirmDialog ref="pushConfirmRef" />

    <!-- Push error detail (+ retry) -->
    <LienPushErrorDialog ref="pushErrorRef" />

    <!-- Bulk push preview (currency list + green CTA) -->
    <LienBulkPushPreviewDialog ref="bulkPushPreviewRef" />

    <!-- Bulk undo confirmation -->
    <LlConfirmationDialog
      ref="bulkUndoConfirmRef"
      title="Undo Submit for Selected Liens"
      :message="bulkUndo.message"
      confirm-text="Delete Checks & Undo Submit"
      toggle-text="Cancel"
      confirm-color="error"
      :show-warning-icon="true"
      data-cy="bulk-undo-confirmation"
    />

    <!-- Bulk delete confirmation -->
    <LlConfirmationDialog
      ref="bulkDeleteConfirmRef"
      title="Delete Selected Liens"
      :message="`Delete ${pluralizeWithCount(selectedIds.length, 'lien')}? This cannot be undone.`"
      toggle-text="Cancel"
      confirm-text="Delete"
      :show-warning-icon="false"
      data-cy="bulk-delete-dialog"
    />

  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue';
import { storeToRefs } from 'pinia';
import { useLienStore } from '@/stores/useLienStore';
import useClientMatterStore from '@/services/useClientMatterStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { formatCurrency, pluralizeWithCount } from '@/services/utils';
import RouteService from '@/services/RouteService';
import type { LienData, LienStatus, LienBulkResponse } from '@/models/lienTypes';
import { LIEN_STATUS_OPTIONS, LIEN_STATUS_LABELS, STATUS_DOT_COLORS, STATUS_STYLES } from '@/constants/lienConstants';
import { resolveQboPushError, isFinalized, isPushBlocked, isPushed, pushBadgeState } from '@/utils/lienHelpers';
import { parseISO, format, isValid } from 'date-fns';
import EventTracker from '@/services/eventTracker';
import LienFormPanel from './LienFormPanel.vue';
import LienPushConfirmDialog from './liens/LienPushConfirmDialog.vue';
import LienPushErrorDialog from './liens/LienPushErrorDialog.vue';
import LienPushBlockedItem from './liens/LienPushBlockedItem.vue';
import LienBulkPushPreviewDialog from './liens/LienBulkPushPreviewDialog.vue';
import { useLienSelection } from './liens/useLienSelection';
import LlCheckbox from '@/components/LlCheckbox.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import SimplePagination from '@/components/SimplePagination.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import AlertBar from '@/components/AlertBar.vue';
import QboIcon from '@/components/QboIcon.vue';

const props = defineProps<{
  stateParams?: Record<string, string>;
  state?: { go: (...args: unknown[]) => void };
  tracking?: object;
}>();

const routeService = new RouteService(props);

const lienStore = useLienStore();
const clientMatterStore = useClientMatterStore();
const { liens, count, summary, isSettlementLocked, isQboConnected, uiState } =
  storeToRefs(lienStore);
const { handleError, handleSuccess, handleWarning } = useSnackbarHandler();

const matterName = ref<string | null>(null);

const matterId = computed<string | null>(
  () => props.stateParams?.matterId ?? props.stateParams?.id ?? null,
);

// Panel state
const panelOpen = ref(false);
const activeLien = ref<LienData | null>(null);
const deleteConfirmRef = ref<InstanceType<typeof LlConfirmationDialog> | null>(
  null,
);
const deleteMessage = ref('Delete this lien? This action cannot be undone.');

// ── Selection + bulk QBO state ───────────────────────────────────────────
const {
  selectedIds,
  selectedLiens,
  allSelected,
  someSelected,
  hasPushableSelected,
  hasUndoableSelected,
  hasAnyPushableOrUndoable,
  pushableSelectedIds,
  blockedSelectedCount,
  undoableSelectedIds,
  isSelected,
  toggle,
  toggleAll,
  clear: clearSelection,
  prune: pruneSelection,
  canRetryLien,
  canUndoLien,
  isPushActionable,
} = useLienSelection(liens, isSettlementLocked);

const pushingIds = reactive(new Set<string>());
const undoingIds = reactive(new Set<string>());
const isBulkPushing = ref(false);
const isBulkUndoing = ref(false);

// Promise-based dialogs. Reusable confirms for undo (destructive → red CTA);
// the push preview is bespoke because it needs currency amounts + a green CTA.
const pushConfirmRef = ref<InstanceType<typeof LienPushConfirmDialog> | null>(null);
const pushErrorRef = ref<InstanceType<typeof LienPushErrorDialog> | null>(null);
const bulkPushPreviewRef = ref<InstanceType<typeof LienBulkPushPreviewDialog> | null>(null);
const bulkUndoConfirmRef = ref<InstanceType<typeof LlConfirmationDialog> | null>(null);
const bulkDeleteConfirmRef = ref<InstanceType<typeof LlConfirmationDialog> | null>(null);
const undoConfirmRef = ref<InstanceType<typeof LlConfirmationDialog> | null>(null);

const undoDialog = ref({ message: '' });
const bulkUndo = ref({ message: '' });

// Per-lien bulk error messages, keyed by lienId. Populated from a bulk op's
// `results[].error` and rendered inline in the QBO Push cell — because a failed
// undo leaves the row `pushed` and some failed pushes never persist a `failed`
// status, so the error has no inline row state of its own to live on.
const bulkErrors = ref<Record<string, string>>({});

// QBO column/actions require an active QuickBooks connection.
const showQboColumn = computed(() => isQboConnected.value);

// The 4 uniform summary-bar cards (Total Liens / Current Total / Original
// Total / Savings). The 5th "Unpushed" card is conditionally visible/colored
// and stays hand-written in the template.
type SummaryMetric = { label: string; value: string | number; valueClass: string; sublabel?: string };

const summaryMetrics = computed<SummaryMetric[]>(() => {
  if (!summary.value) return [];
  return [
    { label: 'Total Liens', value: summary.value.totalLiens, valueClass: 'text-gray-900' },
    { label: 'Current Total', value: formatCurrency(summary.value.totalCurrentAmount), valueClass: 'text-gray-900' },
    { label: 'Original Total', value: formatCurrency(summary.value.totalOriginalAmount), valueClass: 'text-gray-900' },
    {
      label: 'Savings',
      value: formatCurrency(summary.value.totalSavings),
      valueClass: 'text-green-700',
      sublabel: `${Number(summary.value.savingsPercentage).toFixed(2)}% reduction`,
    },
  ];
});

// "Unpushed (Finalized)" summary card (QBO only). Derived from loaded liens.
const unpushedSummary = computed(() => {
  const unpushed = liens.value.filter((l) => isFinalized(l.status));
  return {
    count: unpushed.length,
    value: unpushed.reduce((sum, l) => sum + (l.currentAmount ?? 0), 0),
  };
});

function isRowPushing(lienId: string): boolean {
  return pushingIds.has(lienId);
}

function isRowUndoing(lienId: string): boolean {
  return undoingIds.has(lienId);
}

// Shared by the row checkbox and the kebab-menu trigger: once locked, a row
// stays interactive only for the push/undo action it's still eligible for.
function isRowSelectionLocked(lien: LienData): boolean {
  return isSettlementLocked.value && !isPushActionable(lien) && !canUndoLien(lien);
}

function pushActionLabel(lien: LienData): string {
  return canRetryLien(lien) ? 'Retry Submit' : 'Submit to QuickBooks';
}

function clearLienIdParam(): void {
  routeService.go('.', { lienId: null }, { notify: false, reload: false });
}

function openAdd(): void {
  activeLien.value = null;
  panelOpen.value = true;
  clearLienIdParam();
}

async function openEdit(lien: LienData): Promise<void> {
  if (isSettlementLocked.value) return;
  // If panel is already open, close first so the Transition animates out/in
  if (panelOpen.value) {
    panelOpen.value = false;
    await new Promise((r) => setTimeout(r, 50));
  }
  activeLien.value = lien;
  panelOpen.value = true;
  routeService.go(
    '.',
    { lienId: lien.lienId },
    { notify: false, reload: false },
  );
}

function closePanel(): void {
  panelOpen.value = false;
  activeLien.value = null;
  clearLienIdParam();
}

async function onSaved(): Promise<void> {
  // Panel closes itself via LienFormPanel.submit() → close()
  activeLien.value = null;
  clearLienIdParam();
  if (matterId.value) {
    await lienStore.fetchLiens(matterId.value);
  }
}

async function confirmDelete(lien: LienData): Promise<void> {
  if (!matterId.value) return;
  deleteMessage.value = `Delete lien for "${lien.vendorDisplayName}"? This action cannot be undone.`;
  const confirmed = await deleteConfirmRef.value?.open();
  if (!confirmed) return;
  try {
    await lienStore.deleteLien(lien.lienId);
    if (props.tracking) {
      EventTracker.trackEvent(props.tracking, 'Lien', 'Deleted', {
        matterId: matterId.value,
        lienId: lien.lienId,
        lienType: lien.lienType,
        status: lien.status,
      });
    }
    await lienStore.fetchLiens(matterId.value);
    pruneSelection();
  } catch {
    // error handled in store
  }
}

// ── QBO Submit / undo (single) ─────────────────────────────────────────────
// Row action → confirmation modal → execute (matches the V2 prototype's Submit-modal).
async function handleRowPush(lien: LienData): Promise<void> {
  const confirmed = await pushConfirmRef.value?.open(lien);
  if (!confirmed) return;
  await executePush(lien);
}

async function executePush(lien: LienData): Promise<void> {
  if (!matterId.value || isRowPushing(lien.lienId)) return;
  pushingIds.add(lien.lienId);
  try {
    const updated = await lienStore.pushLien(matterId.value, lien.lienId);
    // The push endpoint may report a handled failure on the returned lien
    // (status stays Finalized + qboPushError populated) rather than throwing.
    if (pushBadgeState(updated) === 'failed') {
      handleError(resolveQboPushError(updated.qboPushError, updated.qboPushError));
    } else {
      handleSuccess(`${lien.vendorDisplayName} submitted to QuickBooks`);
      clearBulkError(lien.lienId);
      trackQbo('Push', lien, { bulk: false });
    }
  } catch (e: unknown) {
    handleError(e);
  } finally {
    pushingIds.delete(lien.lienId);
  }
}

// Failed-lien "Details" link → error detail modal → optional retry.
async function openErrorDetail(lien: LienData): Promise<void> {
  const action = await pushErrorRef.value?.open(lien);
  if (action === 'retry') await executePush(lien);
}

async function handleRowUndo(lien: LienData): Promise<void> {
  if (!matterId.value) return;
  undoDialog.value = {
    message:
      `This will delete the QuickBooks trust check${
        lien.qboTransactionId ? ` (${lien.qboTransactionId})` : ''
      } for "${lien.vendorDisplayName}" — ${formatCurrency(lien.currentAmount)} and revert the lien to Finalized.`,
  };
  const confirmed = await undoConfirmRef.value?.open();
  if (!confirmed) return;
  undoingIds.add(lien.lienId);
  try {
    await lienStore.undoPush(matterId.value, lien.lienId);
    handleSuccess(`Submission undone for ${lien.vendorDisplayName}`);
    clearBulkError(lien.lienId);
    trackQbo('Undo', lien, { bulk: false });
  } catch (e: unknown) {
    handleError(resolveQboPushError(errorKeyOf(e), messageOf(e)));
  } finally {
    undoingIds.delete(lien.lienId);
  }
}

// ── QBO push / undo (bulk) ────────────────────────────────────────────────
const BULK_PUSH_ALL_BLOCKED =
  'The trust balance is too low to cover the selected liens. Add funds to trust before submitting.';

async function handleBulkPush(): Promise<void> {
  if (!matterId.value || !hasPushableSelected.value) return;
  const ids = pushableSelectedIds.value;
  const preview = selectedLiens.value.filter((l) => ids.includes(l.lienId));
  const confirmed = await bulkPushPreviewRef.value?.open(preview);
  if (!confirmed) return;
  isBulkPushing.value = true;
  try {
    const skipped = blockedSelectedCount.value;
    const response = await lienStore.bulkPushLiens(matterId.value, ids);
    applyBulkErrors(response);
    showBulkToast(response, 'push');
    if (skipped > 0) {
      handleWarning(
        `${pluralizeWithCount(skipped, 'lien')} not submitted — the trust balance is too low to cover ${skipped === 1 ? 'it' : 'them'}.`,
      );
    }
    await afterBulkOperation('Push', response, { count: ids.length });
  } catch (e: unknown) {
    handleError(e);
  } finally {
    isBulkPushing.value = false;
  }
}

async function handleBulkUndo(): Promise<void> {
  if (!matterId.value || !hasUndoableSelected.value) return;
  const ids = undoableSelectedIds.value;
  bulkUndo.value = {
    message:
      `This will delete the QuickBooks trust checks for the following ` +
      `${pluralizeWithCount(ids.length, 'lien')} and revert them to Finalized. ` +
      'Reconciled checks cannot be undone and will be reported as failed.',
  };
  const confirmed = await bulkUndoConfirmRef.value?.open();
  if (!confirmed) return;
  isBulkUndoing.value = true;
  try {
    const response = await lienStore.bulkUndoPush(matterId.value, ids);
    applyBulkErrors(response);
    showBulkToast(response, 'undo');
    await afterBulkOperation('Undo', response, { count: ids.length });
  } catch (e: unknown) {
    handleError(e);
  } finally {
    isBulkUndoing.value = false;
  }
}

// ── Bulk delete (local action — no QBO endpoint) ──────────────────────────
async function handleBulkDelete(): Promise<void> {
  if (!matterId.value || isSettlementLocked.value || selectedIds.value.length === 0) return;
  const targets = selectedLiens.value.filter((l) => !isPushed(l.status));
  if (targets.length === 0) {
    handleWarning('Selected liens have been submitted to QuickBooks and cannot be deleted. Undo the submission first.');
    return;
  }
  const confirmed = await bulkDeleteConfirmRef.value?.open();
  if (!confirmed) return;
  try {
    // Local delete has no bulk endpoint; the "no frontend loop" rule applies
    // only to QBO push/undo. Settled so one failure doesn't abort the rest.
    const outcomes = await Promise.allSettled(
      targets.map((l) => lienStore.deleteLien(l.lienId)),
    );
    const deleted = outcomes.filter((o) => o.status === 'fulfilled').length;
    const failed = outcomes.length - deleted;
    if (props.tracking) {
      EventTracker.trackEvent(props.tracking, 'Lien', 'Bulk Deleted', {
        matterId: matterId.value,
        requested: targets.length,
        deleted,
        failed,
      });
    }
    if (failed > 0) handleWarning(`Deleted ${deleted} of ${targets.length}; ${failed} failed.`);
    else handleSuccess(`${pluralizeWithCount(deleted, 'lien')} deleted`);
  } catch (e: unknown) {
    handleError(e);
  } finally {
    clearSelection();
    await lienStore.fetchLiens(matterId.value);
  }
}

function showBulkToast(response: LienBulkResponse, op: 'push' | 'undo'): void {
  const { succeeded, failed, skipped } = response;
  const noun = 'lien';
  const pastVerb = op === 'push' ? 'submitted to QuickBooks' : 'reverted to Finalized';
  if (failed === 0) {
    const parts = [`${pluralizeWithCount(succeeded, noun)} ${pastVerb}`];
    if (skipped > 0) parts.push(`${pluralizeWithCount(skipped, 'lien')} skipped`);
    handleSuccess(parts.join(', '));
  } else if (succeeded === 0) {
    handleError(`All ${pluralizeWithCount(failed, noun)} failed — see inline errors in the table`);
  } else {
    handleWarning(`${pluralizeWithCount(succeeded, noun)} ${pastVerb}, ${pluralizeWithCount(failed, 'lien')} failed — see inline errors in the table`);
  }
}

// Capture each failed lien's error so it renders inline on its row (see the QBO Push cell).
function applyBulkErrors(response: LienBulkResponse): void {
  const errors: Record<string, string> = {};
  response.results.forEach((r) => {
    if (r.error) errors[r.lienId] = resolveQboPushError(r.error);
  });
  bulkErrors.value = errors;
}

async function afterBulkOperation(
  action: 'Push' | 'Undo',
  response: LienBulkResponse,
  meta: { count: number },
): Promise<void> {
  if (props.tracking && matterId.value) {
    EventTracker.trackEvent(props.tracking, 'Lien', `Bulk ${action}`, {
      matterId: matterId.value,
      requested: meta.count,
      success: response.succeeded,
      failed: response.failed,
      skipped: response.skipped,
    });
  }
  clearSelection();
  if (matterId.value) await lienStore.fetchLiens(matterId.value);
}

function trackQbo(
  action: 'Push' | 'Undo',
  lien: LienData,
  meta: { bulk: boolean },
): void {
  if (!props.tracking || !matterId.value) return;
  EventTracker.trackEvent(props.tracking, 'Lien', action, {
    matterId: matterId.value,
    lienId: lien.lienId,
    lienType: lien.lienType,
    bulk: meta.bulk,
  });
}

function errorKeyOf(e: unknown): string | null {
  return (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? null;
}
function messageOf(e: unknown): string | null {
  return (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? null;
}

const lockTooltip = [
  'Liens for this matter are locked because the lien amounts',
  'have already been finalized in the settlement calculator.',
  'Please restart the settlement flow if you need to make any',
  'changes to these liens.',
].join('\n');

const pushedStatusTooltip =
  'This lien has been submitted to QuickBooks. Undo the submission before changing its status.';

// Pagination
const currentPage = computed({
  get: () =>
    Math.floor(lienStore.pagination.offset / lienStore.pagination.limit) + 1,
  set: () => {},
});

function onPageChange(page: number): void {
  if (matterId.value) {
    clearSelection();
    bulkErrors.value = {};
    lienStore.setPage(page, matterId.value);
  }
}

// Sorting
type ColumnDef = {
  label: string;
  dataCy: string;
  orderBy?: string;
  align: 'left' | 'right' | 'center';
};

const COLUMN_DEFS: ColumnDef[] = [
  { label: 'Payee', dataCy: 'lien-header-payee', orderBy: 'vendorDisplayName', align: 'left' },
  { label: 'Description', dataCy: 'lien-header-description', orderBy: 'description', align: 'left' },
  { label: 'Type', dataCy: 'lien-header-type', orderBy: 'lienType', align: 'left' },
  { label: 'Original', dataCy: 'lien-header-original', orderBy: 'originalAmount', align: 'right' },
  { label: 'Negotiated', dataCy: 'lien-header-negotiated', orderBy: 'negotiatedAmount', align: 'right' },
  { label: 'Current', dataCy: 'lien-header-current', orderBy: 'currentAmount', align: 'right' },
  { label: 'Service Start Date', dataCy: 'lien-start-date-header', orderBy: 'dateOfService', align: 'left' },
  { label: 'Service End Date', dataCy: 'lien-end-date-header', orderBy: 'dateOfServiceEnd', align: 'left' },
  { label: 'Status', dataCy: 'lien-header-status', orderBy: 'status', align: 'center' },
];

const COLUMN_ALIGN_CLASS: Record<ColumnDef['align'], string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
};

function onSortClick(orderBy: string): void {
  if (!matterId.value) return;
  clearSelection();
  bulkErrors.value = {};
  lienStore.setSort(orderBy, matterId.value);
}

function sortIcon(orderBy: string): string | null {
  if (lienStore.pagination.orderBy !== orderBy) return null;
  return lienStore.pagination.sortAscending ? 'mdi-arrow-up' : 'mdi-arrow-down';
}

/** Drop the inline bulk error for a lien once it's individually resolved. */
function clearBulkError(lienId: string): void {
  if (bulkErrors.value[lienId]) {
    const next = { ...bulkErrors.value };
    delete next[lienId];
    bulkErrors.value = next;
  }
}

// Selection is component-local — clear it when leaving the tab.
onBeforeUnmount(() => clearSelection());

// Formatting helpers
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = parseISO(iso);
  return isValid(d) ? format(d, 'MM/dd/yyyy') : '—';
}

async function changeStatus(
  lien: LienData,
  newStatus: LienStatus,
): Promise<void> {
  if (newStatus === lien.status || !matterId.value) return;
  const oldStatus = lien.status;
  try {
    await lienStore.updateLienStatus(lien.lienId, newStatus);
    if (props.tracking) {
      EventTracker.trackEvent(props.tracking, 'Lien', 'Status Changed', {
        matterId: matterId.value,
        lienId: lien.lienId,
        oldStatus,
        newStatus,
        lienType: lien.lienType,
      });
    }
    // Only Finalized liens carry a pushBlockedReason, so only entering or leaving it needs the
    // list (and its live trust read) reloaded.
    if (isFinalized(oldStatus) || isFinalized(newStatus)) {
      await lienStore.fetchLiens(matterId.value);
    }
  } catch {
    // error handled in store
  }
}

// Init
onMounted(async () => {
  if (matterId.value) {
    const [matter] = await Promise.all([
      clientMatterStore.getMatterById(matterId.value).catch(() => null),
      lienStore.fetchLiens(matterId.value),
    ]);
    matterName.value = (matter as { name?: string } | null)?.name ?? null;

    // Track tab viewed after data loads (not during skeleton)
    if (props.tracking && !uiState.value.error) {
      EventTracker.trackEvent(props.tracking, 'Liens', 'Tab Viewed', {
        matterId: matterId.value,
        lienCount: count.value,
      });
    }

    // Auto-open edit panel if lienId is in the URL
    const lienId = props.stateParams?.lienId;
    if (lienId) {
      const lien =
        liens.value.find((l) => l.lienId === lienId) ??
        await lienStore.fetchLienById(lienId).catch((): null => null);
      if (lien) {
        openEdit(lien);
      }
    }
  }
});
</script>

<style scoped>
.lien-status-badge {
  font-size: 12px;
  letter-spacing: 0;
}
.lien-status-caret {
  opacity: 0.5;
  margin-left: 2px;
}

/* Some cells have a secondary line (Current → savings, QBO Push → ref), which
   made single-line cells (End Date, Status, Negotiated) drift below the primary
   amount. Top-align every body cell so each column's first line shares one row. */
tbody td { vertical-align: top; }

.lien-status--pending.v-chip    { background-color: #f1f5f9 !important; color: #475569 !important; border: 1px solid #e2e8f0 !important; }
.lien-status--negotiating.v-chip { background-color: #dbeafe !important; color: #1e40af !important; border: 1px solid #bfdbfe !important; }
.lien-status--finalized.v-chip  { background-color: #dcfce7 !important; color: #166534 !important; border: 1px solid #bbf7d0 !important; }
.lien-status--pushedtoqbo.v-chip { background-color: #dcfce7 !important; color: #166534 !important; border: 1px solid #bbf7d0 !important; }
</style>
